import { FaTrash, FaEye } from "react-icons/fa";
import { useState, useEffect } from "react";
import person from "../assets/person.png";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Base_Api = "https://api.hfadigitalcreations.com";
const API_BASE = "https://api.hfadigitalcreations.com/api/users";
const PROFILE_API = "https://api.hfadigitalcreations.com/profile_pic/user";

// Role-specific API endpoints
const ROLE_API_ENDPOINTS = {
  startup: "api/startup",
  investor: "api/investor",
  student: "api/student",
  freelancer: "api/freelancer",
  mentor: "api/mentor",
  hr: "api/hr",
  incubation: "api/incubation",
  legal: "api/legal",
};

const UserTable = ({ activeRole }) => {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingId, setLoadingId] = useState(null);
  const [profileImages, setProfileImages] = useState({});
  const [viewingUser, setViewingUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const usersPerPage = 5;

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_BASE}/getusers`);
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error("Failed to fetch users");
    }
  };

  const fetchProfileImage = async (userId) => {
    try {
      const res = await fetch(`${PROFILE_API}/${userId}`);
      if (!res.ok) throw new Error("No profile");
      const data = await res.json();

      if (data.status && data.data?.image_url) {
        setProfileImages((prev) => ({
          ...prev,
          [userId]: data.data.image_url,
        }));
      } else {
        setProfileImages((prev) => ({ ...prev, [userId]: person }));
      }
    } catch (err) {
      setProfileImages((prev) => ({ ...prev, [userId]: person }));
    }
  };

  // Fetch role-specific details
  const fetchUserDetails = async (userId, roleid, roleName) => {
    try {
      setLoadingDetails(true);

      // Normalize role name
      const normalizedRole = roleName.toLowerCase();

      // Check if endpoint exists
      if (!ROLE_API_ENDPOINTS[normalizedRole]) {
        toast.error("No API endpoint configured for this role");
        return;
      }

      // Construct endpoint dynamically
      const endpoint = `${Base_Api}/${ROLE_API_ENDPOINTS[normalizedRole]}/${roleid}`;
      console.log("Fetching user details from:", endpoint);

      const res = await fetch(endpoint);
      const data = await res.json();

      if (res.ok) {
        setUserDetails(data.data);
        setViewingUser(userId);
      } else {
        throw new Error(data.message || "Failed to fetch details");
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      toast.error(`Error fetching details: ${err.message}`);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(
    (u) => u.role_name?.toLowerCase() === activeRole.toLowerCase()
  );

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const paginatedUsers = filteredUsers.slice(
    startIndex,
    startIndex + usersPerPage
  );

  useEffect(() => {
    paginatedUsers.forEach((user) => {
      if (!profileImages[user.id]) fetchProfileImage(user.id);
    });
  }, [paginatedUsers]);

  function base64ToBlob(base64, contentType = "") {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const approveUser = async (id) => {
    try {
      setLoadingId(id);
      const res = await fetch(`${API_BASE}/accept_user/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });

      if (res.ok) {
        setUsers((prev) =>
          prev.map((u) => (u.id === id ? { ...u, is_accepted: 1 } : u))
        );
        toast.success("User approved successfully!", {
          position: "top-right",
          autoClose: 2000,
        });
        fetchUsers();
      } else {
        toast.error("Approval failed!");
      }
    } catch (err) {
      console.error("Error approving user:", err);
      toast.error("Error approving user");
    } finally {
      setLoadingId(null);
    }
  };

  const declineUser = async (id) => {
    if (!window.confirm("Are you sure you want to decline this user?")) return;

    try {
      setLoadingId(id);
      const res = await fetch(`${API_BASE}/delete_user/${id}`, {
        method: "DELETE",
      });

      if (res.ok) {
        setUsers((prev) => prev.filter((u) => u.id !== id));
        toast.info("User declined!", {
          position: "top-right",
          autoClose: 2000,
        });
        const remaining = filteredUsers.length - 1;
        if (startIndex >= remaining && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } else {
        toast.error("Failed to decline user");
      }
    } catch (err) {
      console.error("Error declining user:", err);
      toast.error("Error declining user");
    } finally {
      setLoadingId(null);
    }
  };

  const exportToCSV = () => {
    const headers = ["Profile", "Name", "Email", "Role", "Status"];
    const rows = filteredUsers.map((user) => [
      "profile",
      user.username,
      user.email,
      user.role_name,
      user.is_accepted === "1" ? "Active" : "Pending",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "users.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("CSV export started!", { position: "top-right" });
  };

  // Render user details based on role
  const renderUserDetails = () => {
    if (!userDetails || !viewingUser) return null;

    const user = users.find((u) => u.id === viewingUser);
    if (!user) return null;

    const roleName = user.role_name.toLowerCase();

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-bold">
              {user.username}'s Details ({user.role_name})
            </h2>
            <button
              onClick={() => {
                setViewingUser(null);
                setUserDetails(null);
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          {loadingDetails ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Common fields for all roles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-gray-700">Profile Image</h3>
                  <img
                    src={profileImages[user.id] || person}
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover mt-2"
                  />
                </div>
                <div className="flex flex-col gap-3 ">
                  <div className="flex flex-col gap-2">
                    <h3 className="font-semibold text-gray-700">Email</h3>
                    <p>{user.email}</p>
                  </div>
                  <div className="inline-block flex-col">
                    <h3 className="font-semibold text-gray-700">Status</h3>
                    <span
                      className={`px-2 py-1 rounded-full text-xs  inline-flex  font-medium ${
                        user.is_accepted === "1"
                          ? "bg-green-100 text-green-800"
                          : "bg-orange-100 text-orange-800"
                      }`}
                    >
                      {user.is_accepted === "1" ? "Active" : "Pending"}
                    </span>
                  </div>
                  <div className="flex flex-col gap-2">
                    <h3 className="font-semibold text-gray-700">Phone</h3>
                    <p>{userDetails.phone_number || "N/A"}</p>
                  </div>
                </div>
              </div>
              {/* Role-specific fields */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-lg mb-3">
                  Role Specific Information
                </h3>

                {roleName === "startup" && renderStartupDetails()}
                {roleName === "investor" && renderInvestorDetails()}
                {roleName === "student" && renderStudentDetails()}
                {roleName === "freelancer" && renderFreelancerDetails()}
                {roleName === "mentor" && renderMentorDetails()}
                {roleName === "hr" && renderHRDetails()}
                {roleName === "incubation" && renderIncubationDetails()}
                {roleName === "legal" && renderLegalDetails()}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Role-specific detail renderers
  const renderStartupDetails = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <h4 className="font-medium text-gray-600">Company Name</h4>
        <p>{userDetails.startup_name || "N/A"}</p>
      </div>
      <div>
        <h4 className="font-medium text-gray-600">Industry Sector</h4>
        <p>{userDetails.industry_sector || "N/A"}</p>
      </div>
      <div>
        <h4 className="font-medium text-gray-600">Current Stage</h4>
        <p>{userDetails.current_stage || "N/A"}</p>
      </div>
      <div>
        <h4 className="font-medium text-gray-600">Funding Status</h4>
        <p>{userDetails.current_funding_status || "N/A"}</p>
      </div>
    </div>
  );

  const renderInvestorDetails = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <h4 className="font-medium text-gray-600">Company Name</h4>
        <p>{userDetails.company_name || "N/A"}</p>
      </div>
      <div>
        <h4 className="font-medium text-gray-600">Investment Range</h4>
        <p>
          {userDetails.investment_min} - {userDetails.investment_max}{" "}
          {userDetails.currency}
        </p>
      </div>
      <div>
        <h4 className="font-medium text-gray-600">Preferred Industries</h4>
        <p>
          {userDetails.preferred_industry
            ? JSON.parse(userDetails.preferred_industry).join(", ")
            : "N/A"}
        </p>
      </div>
    </div>
  );

  const renderStudentDetails = () => {

    let resumeUrl = null;
    if (userDetails?.resume) {
      const blob = base64ToBlob(userDetails.resume, "application/pdf");
      resumeUrl = URL.createObjectURL(blob);
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-600">Full Name</h4>
          <p>{userDetails.full_name || "N/A"}</p>
        </div>
        <div>
          <h4 className="font-medium text-gray-600">Skills</h4>
          <p>
            {userDetails.skills && userDetails.skills.length > 0
              ? userDetails.skills.join(", ")
              : "N/A"}
          </p>
        </div>
        <div>
          <h4 className="font-medium text-gray-600">Work Mode</h4>
          <p>{userDetails.work_mode || "N/A"}</p>
        </div>
        <div>
          <h4 className="font-medium text-gray-600">Availability</h4>
          <p>{userDetails.availability || "N/A"}</p>
        </div>
        <div>
          <h4 className="font-medium text-gray-600">Compensation</h4>
          <p>{userDetails.compensation || "N/A"}</p>
        </div>
        <div>
          <h4 className="font-medium text-gray-600">Professional Profiles</h4>
          {userDetails.professional_profiles ? (
            Object.entries(userDetails.professional_profiles).map(
              ([platform, url]) => (
                <div key={platform} className="flex items-center gap-2 mt-1">
                  <span className="capitalize">{platform}:</span>
                  <a
                    href={url.startsWith("http") ? url : `https://${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:underline"
                  >
                    {url}
                  </a>
                </div>
              )
            )
          ) : (
            <p>N/A</p>
          )}
        </div>

        {/* Resume Download/View Section */}
          <div className="col-span-2">
            <h4 className="font-medium text-gray-600">Resume</h4>
            <a
              href={resumeUrl}
              download="resume.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline cursor-pointer"
            >
              View / Download Resume
            </a>
          </div>
      </div>
    );
  };

  const renderFreelancerDetails = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <h4 className="font-medium text-gray-600">Service Categories</h4>
        <p>
          {userDetails.service_categories
            ? JSON.parse(userDetails.service_categories).join(", ")
            : "N/A"}
        </p>
      </div>
      <div>
        <h4 className="font-medium text-gray-600">Rate</h4>
        <p>
          {userDetails.rate_amount} {userDetails.currency} (
          {userDetails.rate_type})
        </p>
      </div>
    </div>
  );

  const renderMentorDetails = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <h4 className="font-medium text-gray-600">Startup Name</h4>
        <p>{userDetails.startup_name || "N/A"}</p>
      </div>
      <div>
        <h4 className="font-medium text-gray-600">Industry Sector</h4>
        <p>{userDetails.industry_sector || "N/A"}</p>
      </div>
    </div>
  );

  const renderHRDetails = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <h4 className="font-medium text-gray-600">Company Name</h4>
        <p>{userDetails.company_name || "N/A"}</p>
      </div>
      <div>
        <h4 className="font-medium text-gray-600">Recruiter Role</h4>
        <p>{userDetails.recruiter_role || "N/A"}</p>
      </div>
    </div>
  );

  const renderIncubationDetails = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <h4 className="font-medium text-gray-600">Center Name</h4>
        <p>{userDetails.center_name || "N/A"}</p>
      </div>
    </div>
  );

  const renderLegalDetails = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <h4 className="font-medium text-gray-600">Firm Name</h4>
        <p>{userDetails.firm_name || "N/A"}</p>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-lg shadow p-4 mt-4">
      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <input
          type="text"
          placeholder="Search users..."
          className="border p-2 rounded w-full sm:w-1/3"
        />
        <div className="flex gap-2 w-full sm:w-auto justify-end">
          <button className="border px-4 py-2 rounded hover:bg-gray-50">
            Filter
          </button>
          <button
            onClick={exportToCSV}
            className="border px-4 py-2 rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors"
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Profile
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedUsers.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center justify-center">
                    <img
                      src={profileImages[user.id] || person}
                      alt="Profile"
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">{user.username}</td>
                <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                  {user.email}
                </td>
                <td className="px-4 py-3 whitespace-nowrap capitalize">
                  {user.role_name}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      user.is_accepted === "1"
                        ? "bg-green-100 text-green-800"
                        : "bg-orange-100 text-orange-800"
                    }`}
                  >
                    {user.is_accepted === "1" ? "Active" : "Pending"}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() =>
                        fetchUserDetails(user.id, user.role_id, user.role_name)
                      }
                      className="text-gray-500 hover:text-blue-500 transition-colors"
                      title="View Details"
                    >
                      <FaEye className="text-lg" />
                    </button>
                    {user.is_accepted === "1" ? (
                      <button
                        onClick={() => declineUser(user.id)}
                        className="text-gray-500 hover:text-red-500 transition-colors"
                        title="Delete"
                      >
                        <FaTrash className="text-lg" />
                      </button>
                    ) : (
                      <div className="flex gap-2">
                        <button
                          onClick={() => approveUser(user.id)}
                          disabled={loadingId === user.id}
                          className={`px-3 py-1 text-xs rounded border ${
                            loadingId === user.id
                              ? "bg-gray-100 text-gray-400"
                              : "bg-green-50 text-green-600 hover:bg-green-100"
                          }`}
                        >
                          {loadingId === user.id ? "Approving..." : "Approve"}
                        </button>
                        <button
                          onClick={() => declineUser(user.id)}
                          disabled={loadingId === user.id}
                          className={`px-3 py-1 text-xs rounded border ${
                            loadingId === user.id
                              ? "bg-gray-100 text-gray-400"
                              : "bg-red-50 text-red-600 hover:bg-red-100"
                          }`}
                        >
                          {loadingId === user.id ? "Declining..." : "Decline"}
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">
        <p className="text-gray-600 text-sm">
          Showing {startIndex + 1} to{" "}
          {Math.min(startIndex + usersPerPage, filteredUsers.length)} of{" "}
          {filteredUsers.length} entries
        </p>
        <div className="flex items-center gap-1">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent"
          >
            &lt;
          </button>
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              onClick={() => goToPage(index + 1)}
              className={`w-8 h-8 rounded flex items-center justify-center border ${
                currentPage === index + 1
                  ? "bg-blue-500 text-white border-blue-500"
                  : "hover:bg-gray-100"
              }`}
            >
              {index + 1}
            </button>
          ))}
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:hover:bg-transparent"
          >
            &gt;
          </button>
        </div>
      </div>

      {/* User Details Modal */}
      {renderUserDetails()}

      <ToastContainer />
    </div>
  );
};

export default UserTable;
